import { supabase } from '@/integrations/supabase/client';

export interface CommunicationTemplate {
  id: string;
  restaurant_id: string;
  name: string;
  type: 'sms_order_status' | 'whatsapp_receipt' | 'feedback_request' | 'review_request' | 'custom';
  subject?: string;
  message: string;
  variables: Record<string, string>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommunicationLog {
  id: string;
  restaurant_id: string;
  branch_id?: string;
  order_id?: string;
  customer_phone: string;
  customer_name?: string;
  communication_type: 'sms' | 'whatsapp' | 'email';
  template_id?: string;
  subject?: string;
  message: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed';
  provider?: string;
  provider_response?: any;
  sent_at?: string;
  delivered_at?: string;
  failed_at?: string;
  failure_reason?: string;
  cost_amount: number;
  cost_currency: string;
  created_at: string;
  updated_at: string;
}

export interface CustomerFeedback {
  id: string;
  restaurant_id: string;
  branch_id?: string;
  order_id?: string;
  customer_phone: string;
  customer_name?: string;
  rating?: number;
  feedback_text?: string;
  feedback_type: 'general' | 'food_quality' | 'service' | 'delivery' | 'complaint';
  is_anonymous: boolean;
  response_token?: string;
  responded_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ReviewRequest {
  id: string;
  restaurant_id: string;
  branch_id?: string;
  order_id?: string;
  customer_phone: string;
  customer_name?: string;
  platform: 'google' | 'facebook' | 'yelp' | 'tripadvisor';
  request_sent_at?: string;
  clicked_at?: string;
  review_submitted_at?: string;
  review_rating?: number;
  created_at: string;
  updated_at: string;
}

export interface CommunicationSettings {
  id: string;
  restaurant_id: string;
  sms_enabled: boolean;
  sms_provider: string;
  sms_api_key?: string;
  sms_api_secret?: string;
  sms_from_number?: string;
  whatsapp_enabled: boolean;
  whatsapp_api_key?: string;
  whatsapp_from_number?: string;
  auto_send_order_confirmed: boolean;
  auto_send_order_preparing: boolean;
  auto_send_order_ready: boolean;
  auto_send_order_completed: boolean;
  auto_send_receipt: boolean;
  auto_request_feedback: boolean;
  auto_request_review: boolean;
  feedback_delay_hours: number;
  review_delay_hours: number;
  created_at: string;
  updated_at: string;
}

class CommunicationServiceError extends Error {
  constructor(message: string, public cause?: unknown) {
    super(message);
    this.name = 'CommunicationServiceError';
  }
}

// Template Management
export async function fetchCommunicationTemplates(restaurantId: string): Promise<CommunicationTemplate[]> {
  try {
    const { data, error } = await supabase
      .from('communication_templates')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('name');

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch communication templates', error);
  }
}

export async function createCommunicationTemplate(template: Omit<CommunicationTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<CommunicationTemplate> {
  try {
    const { data, error } = await supabase
      .from('communication_templates')
      .insert(template)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to create communication template', error);
  }
}

export async function updateCommunicationTemplate(id: string, updates: Partial<CommunicationTemplate>): Promise<CommunicationTemplate> {
  try {
    const { data, error } = await supabase
      .from('communication_templates')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to update communication template', error);
  }
}

export async function deleteCommunicationTemplate(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('communication_templates')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new CommunicationServiceError('Failed to delete communication template', error);
  }
}

// Communication Settings
export async function fetchCommunicationSettings(restaurantId: string): Promise<CommunicationSettings | null> {
  try {
    const { data, error } = await supabase
      .from('communication_settings')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch communication settings', error);
  }
}

export async function upsertCommunicationSettings(settings: Omit<CommunicationSettings, 'id' | 'created_at' | 'updated_at'>): Promise<CommunicationSettings> {
  try {
    const { data, error } = await supabase
      .from('communication_settings')
      .upsert(
        { ...settings, updated_at: new Date().toISOString() },
        { onConflict: 'restaurant_id' }
      )
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to save communication settings', error);
  }
}

// Order Status Communication
export async function sendOrderStatusCommunication(
  orderId: string, 
  status: string, 
  communicationType: 'sms' | 'whatsapp' = 'sms'
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const { data, error } = await supabase.rpc('send_order_status_communication', {
      p_order_id: orderId,
      p_status: status,
      p_communication_type: communicationType
    });

    if (error) throw error;
    
    const result = data as { success: boolean; communication_id?: string; message?: string; phone?: string; provider_config?: any; error?: string };
    
    if (result.success && result.provider_config && result.phone && result.message) {
      // If we have provider config, attempt to send via external service
      try {
        await sendSMSViaTwilio(
          result.phone, 
          result.message, 
          result.provider_config.api_key,
          result.provider_config.api_secret || result.provider_config.auth_token,
          result.provider_config.from_number
        );
        
        // Update communication log to mark as sent
        if (result.communication_id) {
          await updateCommunicationLogStatus(result.communication_id, 'sent');
        }
        
        return { success: true, message: 'Message sent successfully' };
      } catch (sendError) {
        // Update communication log to mark as failed
        if (result.communication_id) {
          await updateCommunicationLogStatus(result.communication_id, 'failed', sendError instanceof Error ? sendError.message : 'Send failed');
        }
        return { success: false, error: sendError instanceof Error ? sendError.message : 'Failed to send message' };
      }
    }
    
    return result;
  } catch (error) {
    throw new CommunicationServiceError('Failed to send order status communication', error);
  }
}

// SMS Service Integration (Twilio)
async function sendSMSViaTwilio(
  to: string, 
  message: string, 
  accountSid: string, 
  authToken: string, 
  fromNumber: string
): Promise<void> {
  try {
    // This is a placeholder for Twilio integration
    // In a real implementation, you would use the Twilio API
    const response = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Messages.json`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${btoa(`${accountSid}:${authToken}`)}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        To: to,
        From: fromNumber,
        Body: message,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Twilio API error: ${errorData.message || response.statusText}`);
    }
  } catch (error) {
    throw new Error(`Failed to send SMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Communication Log Management
export async function fetchCommunicationLog(restaurantId: string, filters?: {
  branchId?: string;
  orderId?: string;
  status?: string;
  limit?: number;
}): Promise<CommunicationLog[]> {
  try {
    let query = supabase
      .from('communication_log')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    if (filters?.branchId) {
      query = query.eq('branch_id', filters.branchId);
    }
    if (filters?.orderId) {
      query = query.eq('order_id', filters.orderId);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch communication log', error);
  }
}

export async function updateCommunicationLogStatus(
  communicationId: string, 
  status: 'sent' | 'delivered' | 'failed',
  failureReason?: string
): Promise<void> {
  try {
    const updates: any = {
      status,
      updated_at: new Date().toISOString()
    };

    if (status === 'sent') {
      updates.sent_at = new Date().toISOString();
    } else if (status === 'delivered') {
      updates.delivered_at = new Date().toISOString();
    } else if (status === 'failed') {
      updates.failed_at = new Date().toISOString();
      if (failureReason) {
        updates.failure_reason = failureReason;
      }
    }

    const { error } = await supabase
      .from('communication_log')
      .update(updates)
      .eq('id', communicationId);

    if (error) throw error;
  } catch (error) {
    throw new CommunicationServiceError('Failed to update communication log status', error);
  }
}

// Customer Feedback
export async function fetchCustomerFeedback(restaurantId: string, filters?: {
  branchId?: string;
  orderId?: string;
  rating?: number;
  feedbackType?: string;
  limit?: number;
}): Promise<CustomerFeedback[]> {
  try {
    let query = supabase
      .from('customer_feedback')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    if (filters?.branchId) {
      query = query.eq('branch_id', filters.branchId);
    }
    if (filters?.orderId) {
      query = query.eq('order_id', filters.orderId);
    }
    if (filters?.rating) {
      query = query.eq('rating', filters.rating);
    }
    if (filters?.feedbackType) {
      query = query.eq('feedback_type', filters.feedbackType);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch customer feedback', error);
  }
}

export async function submitCustomerFeedback(
  token: string,
  rating: number,
  feedbackText?: string,
  feedbackType: string = 'general'
): Promise<void> {
  try {
    const { error } = await supabase
      .from('customer_feedback')
      .update({
        rating,
        feedback_text: feedbackText,
        feedback_type: feedbackType,
        responded_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('response_token', token);

    if (error) throw error;
  } catch (error) {
    throw new CommunicationServiceError('Failed to submit customer feedback', error);
  }
}

export async function getFeedbackByToken(token: string): Promise<CustomerFeedback | null> {
  try {
    const { data, error } = await supabase
      .from('customer_feedback')
      .select('*')
      .eq('response_token', token)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch feedback by token', error);
  }
}

// Review Requests
export async function fetchReviewRequests(restaurantId: string, filters?: {
  branchId?: string;
  platform?: string;
  limit?: number;
}): Promise<ReviewRequest[]> {
  try {
    let query = supabase
      .from('review_requests')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    if (filters?.branchId) {
      query = query.eq('branch_id', filters.branchId);
    }
    if (filters?.platform) {
      query = query.eq('platform', filters.platform);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch review requests', error);
  }
}

export async function createReviewRequest(reviewRequest: Omit<ReviewRequest, 'id' | 'created_at' | 'updated_at'>): Promise<ReviewRequest> {
  try {
    const { data, error } = await supabase
      .from('review_requests')
      .insert(reviewRequest)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to create review request', error);
  }
}

// Generate feedback link
export async function generateFeedbackLink(orderId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase.rpc('generate_feedback_link', {
      p_order_id: orderId
    });

    if (error) throw error;
    return data;
  } catch (error) {
    throw new CommunicationServiceError('Failed to generate feedback link', error);
  }
}

// WhatsApp Integration
export async function sendWhatsAppMessage(
  to: string,
  message: string,
  whatsappApiKey?: string,
  fromNumber?: string
): Promise<void> {
  try {
    // This is a placeholder for WhatsApp Business API integration
    // In a real implementation, you would use the WhatsApp Business API
    console.log('Sending WhatsApp message:', { to, message, fromNumber });
    
    // For now, we'll use the existing WhatsApp Desktop integration
    const whatsappUrl = `whatsapp://send?phone=${encodeURIComponent(to)}&text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  } catch (error) {
    throw new Error(`Failed to send WhatsApp message: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Communication Analytics
export async function getCommunicationAnalytics(restaurantId: string, timeRange: number = 30): Promise<{
  totalSent: number;
  deliveryRate: number;
  costTotal: number;
  feedbackCount: number;
  averageRating: number;
  reviewRequests: number;
}> {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - timeRange);

    // Get communication stats
    const { data: commLog, error: commError } = await supabase
      .from('communication_log')
      .select('status, cost_amount')
      .eq('restaurant_id', restaurantId)
      .gte('created_at', startDate.toISOString());

    if (commError) throw commError;

    // Get feedback stats
    const { data: feedback, error: feedbackError } = await supabase
      .from('customer_feedback')
      .select('rating')
      .eq('restaurant_id', restaurantId)
      .gte('created_at', startDate.toISOString())
      .not('rating', 'is', null);

    if (feedbackError) throw feedbackError;

    // Get review request stats
    const { data: reviews, error: reviewError } = await supabase
      .from('review_requests')
      .select('id')
      .eq('restaurant_id', restaurantId)
      .gte('created_at', startDate.toISOString());

    if (reviewError) throw reviewError;

    const totalSent = commLog?.length || 0;
    const delivered = commLog?.filter(log => log.status === 'delivered').length || 0;
    const deliveryRate = totalSent > 0 ? (delivered / totalSent) * 100 : 0;
    const costTotal = commLog?.reduce((sum, log) => sum + (log.cost_amount || 0), 0) || 0;
    const feedbackCount = feedback?.length || 0;
    const averageRating = feedbackCount > 0 
      ? feedback!.reduce((sum, fb) => sum + (fb.rating || 0), 0) / feedbackCount 
      : 0;
    const reviewRequests = reviews?.length || 0;

    return {
      totalSent,
      deliveryRate,
      costTotal,
      feedbackCount,
      averageRating,
      reviewRequests
    };
  } catch (error) {
    throw new CommunicationServiceError('Failed to fetch communication analytics', error);
  }
}

